import {
  configureProjectFlaggersForOnboardingUseCase,
  emptyFlaggerCoverageRow,
  FLAGGER_DEFAULT_SAMPLING,
  FLAGGER_DISPLAY,
  FLAGGER_STRATEGY_SLUGS,
  FlaggerCoverageRepository,
  type FlaggerCoverageRow,
  FlaggerRepository,
  type FlaggerSlug,
  updateFlaggerUseCase,
} from "@domain/flaggers"
import { ProjectId } from "@domain/shared"
import { RedisCacheStoreLive } from "@platform/cache-redis"
import { FlaggerCoverageRepositoryLive } from "@platform/db-clickhouse"
import { FlaggerRepositoryLive, OutboxEventWriterLive } from "@platform/db-postgres"
import { withTracing } from "@repo/observability"
import { createServerFn } from "@tanstack/react-start"
import { Effect, Layer } from "effect"
import { z } from "zod"
import { requireSession } from "../../server/auth.ts"
import { getClickhouseClient, getPostgresClient, getRedisClient } from "../../server/clients.ts"
import { requireScopedSession, resolveOrgScope } from "../../server/resolve-org-scope.ts"
import { withScopedClickHouse } from "../../server/scoped-clickhouse.ts"
import { withScopedPostgres } from "../../server/scoped-postgres.ts"

const humanizeSlug = (slug: string) => slug.replaceAll("-", " ").replace(/\b\w/g, (letter) => letter.toUpperCase())

const toFlaggerRecord = (flagger: {
  readonly id: string
  readonly organizationId: string
  readonly projectId: string
  readonly slug: FlaggerSlug
  readonly enabled: boolean
  readonly sampling: number
  readonly createdAt: Date
  readonly updatedAt: Date
}) => {
  const display = FLAGGER_DISPLAY[flagger.slug]

  return {
    id: flagger.id,
    organizationId: flagger.organizationId,
    projectId: flagger.projectId,
    slug: flagger.slug,
    name: display?.name ?? humanizeSlug(flagger.slug),
    description: display?.description ?? "Flags matching trace behavior for review.",
    instructions: display?.instructions ?? "Runs deterministically from telemetry data and does not call an LLM.",
    enabled: flagger.enabled,
    sampling: flagger.sampling,
    mode: display?.mode ?? "deterministic",
    suppressedBy: [...(display?.suppressedBy ?? [])],
    createdAt: flagger.createdAt.toISOString(),
    updatedAt: flagger.updatedAt.toISOString(),
  }
}

export type FlaggerRecord = ReturnType<typeof toFlaggerRecord>

const MAX_FLAGGER_COVERAGE_WINDOW_MS = 28 * 24 * 60 * 60 * 1000

const flaggerCoverageInputSchema = z
  .object({
    projectId: z.string(),
    fromIso: z.string().datetime(),
    toIso: z.string().datetime(),
  })
  .refine(({ fromIso, toIso }) => Date.parse(fromIso) < Date.parse(toIso), {
    message: "Coverage window must end after it starts",
  })
  .refine(({ fromIso, toIso }) => Date.parse(toIso) - Date.parse(fromIso) <= MAX_FLAGGER_COVERAGE_WINDOW_MS, {
    message: "Coverage window cannot exceed 28 days",
  })

type FlaggerCoverageRecord = {
  readonly fromIso: string
  readonly toIso: string
  readonly recordingSinceIso: string | null
  readonly sessionsBeforeRecording: number
  readonly rows: readonly FlaggerCoverageRow[]
}

// A registered strategy a project has no stored row for yet: shown disabled in
// settings without writing to the DB. The placeholder id is the slug; the real
// row (with a real id) replaces it on the next read once the user enables it.
const toMissingFlaggerRecord = (slug: FlaggerSlug, organizationId: string, projectId: string): FlaggerRecord => {
  const display = FLAGGER_DISPLAY[slug]
  const epoch = new Date(0).toISOString()

  return {
    id: slug,
    organizationId,
    projectId,
    slug,
    name: display?.name ?? humanizeSlug(slug),
    description: display?.description ?? "Flags matching trace behavior for review.",
    instructions: display?.instructions ?? "Runs deterministically from telemetry data and does not call an LLM.",
    enabled: false,
    sampling: FLAGGER_DEFAULT_SAMPLING,
    mode: display?.mode ?? "deterministic",
    suppressedBy: [...(display?.suppressedBy ?? [])],
    createdAt: epoch,
    updatedAt: epoch,
  }
}

const toAvailableFlaggerRecord = (slug: FlaggerSlug) => {
  const display = FLAGGER_DISPLAY[slug]

  return {
    slug,
    name: display?.name ?? humanizeSlug(slug),
    description: display?.description ?? "Flags matching trace behavior for review.",
    mode: display?.mode ?? "deterministic",
  }
}

type AvailableFlaggerRecord = ReturnType<typeof toAvailableFlaggerRecord>

export const listAvailableFlaggers = createServerFn({ method: "GET" }).handler(
  async (): Promise<readonly AvailableFlaggerRecord[]> => {
    await requireSession()
    return FLAGGER_STRATEGY_SLUGS.map(toAvailableFlaggerRecord)
  },
)

export const getProjectFlaggerCoverage = createServerFn({ method: "GET" })
  .inputValidator(flaggerCoverageInputSchema)
  .handler(async ({ data, context }): Promise<FlaggerCoverageRecord> => {
    const orgId = await resolveOrgScope(context)
    const projectId = ProjectId(data.projectId)
    const report = await Effect.runPromise(
      Effect.gen(function* () {
        const repository = yield* FlaggerCoverageRepository
        return yield* repository.getProjectCoverage({
          organizationId: orgId,
          projectId,
          from: new Date(data.fromIso),
          to: new Date(data.toIso),
        })
      }).pipe(withScopedClickHouse(FlaggerCoverageRepositoryLive, getClickhouseClient(), orgId), withTracing),
    )
    const rowsBySlug = new Map(report.rows.map((row) => [row.flaggerSlug, row]))
    return {
      fromIso: report.from.toISOString(),
      toIso: report.to.toISOString(),
      recordingSinceIso: report.recordingSince?.toISOString() ?? null,
      sessionsBeforeRecording: report.sessionsBeforeRecording,
      rows: FLAGGER_STRATEGY_SLUGS.map(
        (flaggerSlug) =>
          rowsBySlug.get(flaggerSlug) ??
          emptyFlaggerCoverageRow({ flaggerSlug, eligibleSessions: report.eligibleSessions }),
      ),
    }
  })

export const listFlaggersByProject = createServerFn({ method: "GET" })
  .inputValidator(z.object({ projectId: z.string() }))
  .handler(async ({ data, context }): Promise<readonly FlaggerRecord[]> => {
    const orgId = await resolveOrgScope(context)
    const projectId = ProjectId(data.projectId)
    const client = getPostgresClient()

    const stored = await Effect.runPromise(
      Effect.gen(function* () {
        const repo = yield* FlaggerRepository
        return yield* repo.listByProject({ projectId })
      }).pipe(withScopedPostgres(FlaggerRepositoryLive, client, orgId), withTracing),
    )

    const storedBySlug = new Map(stored.map((flagger) => [flagger.slug, flagger]))
    return FLAGGER_STRATEGY_SLUGS.map((slug) => {
      const row = storedBySlug.get(slug)
      return row ? toFlaggerRecord(row) : toMissingFlaggerRecord(slug, orgId, data.projectId)
    })
  })

export const configureProjectFlaggersForOnboarding = createServerFn({
  method: "POST",
})
  .inputValidator(
    z.object({
      projectId: z.string(),
      enabledSlugs: z.array(z.enum(FLAGGER_STRATEGY_SLUGS)),
    }),
  )
  .handler(async ({ data, context }): Promise<void> => {
    const { userId, organizationId: orgId } = await requireScopedSession(context)
    const projectId = ProjectId(data.projectId)
    const client = getPostgresClient()

    await Effect.runPromise(
      configureProjectFlaggersForOnboardingUseCase({
        organizationId: orgId,
        projectId,
        enabledSlugs: data.enabledSlugs,
        actorUserId: userId,
      }).pipe(
        withScopedPostgres(Layer.mergeAll(FlaggerRepositoryLive, OutboxEventWriterLive), client, orgId),
        Effect.provide(RedisCacheStoreLive(getRedisClient())),
        withTracing,
      ),
    )
  })

export const updateFlagger = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      projectId: z.string(),
      slug: z.enum(FLAGGER_STRATEGY_SLUGS),
      enabled: z.boolean(),
      sampling: z.number().int().min(0).max(100),
    }),
  )
  .handler(async ({ data, context }): Promise<FlaggerRecord | null> => {
    const { userId, organizationId: orgId } = await requireScopedSession(context)
    const projectId = ProjectId(data.projectId)
    const client = getPostgresClient()

    const flagger = await Effect.runPromise(
      updateFlaggerUseCase({
        organizationId: orgId,
        projectId,
        slug: data.slug,
        enabled: data.enabled,
        sampling: data.sampling,
        actorUserId: userId,
      }).pipe(
        withScopedPostgres(Layer.mergeAll(FlaggerRepositoryLive, OutboxEventWriterLive), client, orgId),
        Effect.provide(RedisCacheStoreLive(getRedisClient())),
        withTracing,
      ),
    )

    return flagger ? toFlaggerRecord(flagger) : null
  })
